import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  BookOpen, Moon, Maximize, Minimize, Share2, ChevronLeft, ChevronRight, X,
  Settings, Clock, Music, Volume2, VolumeX, Play, Pause, Trash2, RefreshCw,
  Loader2, Book, Link2, Copy, Image, Star, Pencil, Save, XCircle, 
  Download, Mic, MicOff, FileText, Printer
} from "lucide-react";
import { PageTransition, JellyButton, MagicLoader, CreditsModal } from "../components/common";
import { api, getImageUrl } from "../utils/api";
import { genreIcons, BACKGROUND_MUSIC, VOICE_SPEEDS } from "../utils/constants";
import { useCredits } from "../context/CreditsContext";
import { useUnlockNotifications } from "../context/UnlockNotificationContext";
import usePageTitle from "../hooks/usePageTitle";

const StoryReaderPage = ({ isSharedView = false, sharedStory = null }) => {
  usePageTitle(isSharedView ? "Shared Story" : "Read Your Story");
  const location = useLocation();
  const navigate = useNavigate();
  const { deductCredits, refreshCredits } = useCredits();
  const { triggerUnlockCheck } = useUnlockNotifications();
  const [story, setStory] = useState(sharedStory);
  const [loading, setLoading] = useState(!sharedStory);
  const [currentPage, setCurrentPage] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingCover, setRegeneratingCover] = useState(false);
  const storyId = location.pathname.split("/story/")[1];
  
  // User permissions state
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [showFixImagesModal, setShowFixImagesModal] = useState(false);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  
  // Reading Mode states
  const [readingMode, setReadingMode] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [musicVolume, setMusicVolume] = useState(0.3);
  const [pageInterval, setPageInterval] = useState(8);
  const [showSettings, setShowSettings] = useState(false);
  
  // Fullscreen Book Mode states
  const [fullscreenMode, setFullscreenMode] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isFeaturingStory, setIsFeaturingStory] = useState(false);
  
  // TTS Narration states
  const [narrationEnabled, setNarrationEnabled] = useState(false);
  const [isNarrating, setIsNarrating] = useState(false);
  const [voiceSpeed, setVoiceSpeed] = useState("normal");
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  
  // Edit Mode states
  const [editMode, setEditMode] = useState(false);
  const [editedPages, setEditedPages] = useState({});  // { pageNumber: newText }
  const [editedTitle, setEditedTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Credits modal state
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsModalData, setCreditsModalData] = useState({
    blockingReason: "insufficient_credits",
    creditsRequired: 0,
    creditsAvailable: 0,
    limitsData: {},
    planName: "Free"
  });
  
  // AI TTS Narration states
  const [aiNarrationEnabled, setAiNarrationEnabled] = useState(false);
  const [aiNarrationLoading, setAiNarrationLoading] = useState(false);
  const [aiNarrationAudio, setAiNarrationAudio] = useState({}); // { pageNumber: base64Audio }
  const [selectedAiVoice, setSelectedAiVoice] = useState("fable");
  const [aiVoices, setAiVoices] = useState([]);
  const aiAudioRef = useRef(null);
  const [isAiAudioPlaying, setIsAiAudioPlaying] = useState(false);
  const [hasAudioLoaded, setHasAudioLoaded] = useState(false);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  
  // PDF Export states
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  
  // Touch/Swipe states for mobile and tablet
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndX = useRef(0);
  const touchEndY = useRef(0);
  const isSwiping = useRef(false);
  const bookContainerRef = useRef(null);
  const [selectedMusic, setSelectedMusic] = useState("lullaby");
  const [swipeDirection, setSwipeDirection] = useState(null); // For visual feedback
  
  const audioRef = useRef(null);
  const autoPlayTimerRef = useRef(null);

  // Image Modification states
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyPageNum, setModifyPageNum] = useState(null);
  const [modificationPrompt, setModificationPrompt] = useState("");
  const [isModifying, setIsModifying] = useState(false);

  // Load current user info for permission checks
  useEffect(() => {
    const loadUserInfo = async () => {
      if (isSharedView) return;
      try {
        // Get user ID from auth/me endpoint
        const userRes = await api.get("/auth/me");
        setCurrentUserId(userRes.data.user_id);
        setIsMasterAdmin(userRes.data.is_master_admin || false);
      } catch (e) {
        console.log("Could not load user info");
      }
    };
    loadUserInfo();
  }, [isSharedView]);

  // Load AI TTS voices
  useEffect(() => {
    const loadAiVoices = async () => {
      try {
        const res = await api.get("/tts/voices");
        setAiVoices(Object.entries(res.data.voices).map(([id, info]) => ({ id, ...info })));
        setSelectedAiVoice(res.data.default);
      } catch (e) {
        console.log("AI TTS voices not available");
      }
    };
    loadAiVoices();
  }, []);

  // Initialize TTS voices
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices() || [];
      const englishVoices = voices.filter(v => v.lang.startsWith('en'));
      setAvailableVoices(englishVoices);
      const preferredVoice = englishVoices.find(v => 
        v.name.includes('Female') || v.name.includes('Samantha') || v.name.includes('Karen')
      ) || englishVoices[0];
      if (preferredVoice) setSelectedVoice(preferredVoice);
    };
    
    loadVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices);
    
    return () => {
      window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices);
      window.speechSynthesis?.cancel();
    };
  }, []);

  useEffect(() => {
    if (!isSharedView && storyId) loadStory();
    if (sharedStory) {
      setStory(sharedStory);
      setShareToken(sharedStory.share_token);
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (aiAudioRef.current) {
        aiAudioRef.current.pause();
        aiAudioRef.current = null;
      }
      if (autoPlayTimerRef.current) {
        clearTimeout(autoPlayTimerRef.current);
      }
      window.speechSynthesis?.cancel();
    };
  }, [storyId, isSharedView, sharedStory]);

  // Stop AI narration when changing pages
  useEffect(() => {
    if (aiAudioRef.current) {
      aiAudioRef.current.pause();
      aiAudioRef.current = null;
      setIsAiAudioPlaying(false);
      setHasAudioLoaded(false);
    }
  }, [currentPage]);

  // Poll for updates while generating
  useEffect(() => {
    if (!isSharedView && story && (story.status === "generating" || story.status === "illustrating" || story.status === "regenerating")) {
      const interval = setInterval(loadStory, 5000);
      return () => clearInterval(interval);
    }
  }, [story?.status, isSharedView]);

  // TTS Narration effect
  useEffect(() => {
    if (narrationEnabled && isNarrating && story && currentPage > 0) {
      const pages = story.pages || [];
      const pageData = pages[currentPage - 1];
      if (pageData?.text) {
        speakText(pageData.text);
      }
    }
  }, [currentPage, narrationEnabled, isNarrating]);

  // Auto-advance pages in reading mode
  useEffect(() => {
    if (readingMode && isPlaying && story) {
      const totalPages = story.pages?.length || 0;
      
      autoPlayTimerRef.current = setTimeout(() => {
        if (currentPage < totalPages) {
          setCurrentPage(prev => prev + 1);
        } else {
          setIsPlaying(false);
          toast.success("Story finished! 🌟");
          // Check for new unlocks after completing a story
          triggerUnlockCheck();
        }
      }, pageInterval * 1000);
      
      return () => {
        if (autoPlayTimerRef.current) {
          clearTimeout(autoPlayTimerRef.current);
        }
      };
    }
  }, [readingMode, isPlaying, currentPage, pageInterval, story]);

  // Handle background music
  useEffect(() => {
    if (readingMode && isPlaying && musicEnabled) {
      if (!audioRef.current) {
        audioRef.current = new Audio(BACKGROUND_MUSIC[selectedMusic]);
        audioRef.current.loop = true;
        audioRef.current.volume = musicVolume;
      }
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    } else if (audioRef.current) {
      audioRef.current.pause();
    }
    
    return () => {
      if (audioRef.current && !isPlaying) {
        audioRef.current.pause();
      }
    };
  }, [readingMode, isPlaying, musicEnabled, selectedMusic]);

  // Update volume when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = musicVolume;
    }
  }, [musicVolume]);

  const loadStory = async () => {
    try {
      const res = await api.get(`/stories/${storyId}`);
      setStory(res.data);
      if (res.data.share_token) {
        setShareToken(res.data.share_token);
      }
      setIsFeatured(res.data.is_featured || false);
    } catch (e) {
      toast.error("Story not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this storybook?")) return;
    try {
      await api.delete(`/stories/${storyId}`);
      toast.success("Storybook deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleRegeneratePage = async (pageNum) => {
    // Check credits before regenerating
    try {
      const checkRes = await api.get("/subscription/check-limits?action=illustration_regenerate&amount=1");
      
      if (!checkRes.data.can_proceed) {
        setCreditsModalData({
          blockingReason: checkRes.data.blocking_reason,
          creditsRequired: checkRes.data.credits.required,
          creditsAvailable: checkRes.data.credits.available,
          limitsData: checkRes.data.limits,
          planName: checkRes.data.plan_name
        });
        setShowCreditsModal(true);
        return;
      }
    } catch (e) {
      console.error("Failed to check limits:", e);
    }
    
    setRegenerating(true);
    try {
      await api.post(`/stories/${storyId}/regenerate-page/${pageNum}`);
      // Deduct credits immediately in UI (2 credits for illustration regenerate)
      deductCredits(2);
      toast.success("Regenerating illustration...");
      loadStory();
    } catch (e) {
      refreshCredits();
      if (e.response?.status === 402) {
        setCreditsModalData({
          blockingReason: "insufficient_credits",
          creditsRequired: 2,
          creditsAvailable: 0,
          limitsData: {},
          planName: "Free"
        });
        setShowCreditsModal(true);
      } else {
        toast.error("Failed to regenerate");
      }
    } finally {
      setRegenerating(false);
    }
  };

  // Open modify modal for a specific page
  const openModifyModal = (pageNum) => {
    setModifyPageNum(pageNum);
    setModificationPrompt("");
    setShowModifyModal(true);
  };

  // Handle image modification with custom prompt
  const handleModifyImage = async () => {
    if (!modificationPrompt.trim()) {
      toast.error("Please enter a modification description");
      return;
    }

    // Check credits before modifying
    try {
      const checkRes = await api.get("/subscription/check-limits?action=illustration_regenerate&amount=1");
      
      if (!checkRes.data.can_proceed) {
        setCreditsModalData({
          blockingReason: checkRes.data.blocking_reason,
          creditsRequired: checkRes.data.credits.required,
          creditsAvailable: checkRes.data.credits.available,
          limitsData: checkRes.data.limits,
          planName: checkRes.data.plan_name
        });
        setShowCreditsModal(true);
        return;
      }
    } catch (e) {
      console.error("Failed to check limits:", e);
    }
    
    setIsModifying(true);
    try {
      const res = await api.post(`/stories/${storyId}/modify-page/${modifyPageNum}`, {
        modification_prompt: modificationPrompt.trim()
      });
      // Deduct credits immediately in UI (2 credits for illustration modification)
      deductCredits(2);
      toast.success(`Image modified: "${modificationPrompt.trim().substring(0, 30)}${modificationPrompt.length > 30 ? '...' : ''}"`);
      setShowModifyModal(false);
      setModificationPrompt("");
      loadStory();
    } catch (e) {
      refreshCredits();
      if (e.response?.status === 402) {
        setCreditsModalData({
          blockingReason: "insufficient_credits",
          creditsRequired: 2,
          creditsAvailable: 0,
          limitsData: {},
          planName: "Free"
        });
        setShowCreditsModal(true);
      } else {
        toast.error(e.response?.data?.detail || "Failed to modify image");
      }
    } finally {
      setIsModifying(false);
    }
  };

  const handleRegenerateCover = async () => {
    // Check credits before regenerating cover
    try {
      const checkRes = await api.get("/subscription/check-limits?action=illustration_regenerate&amount=1");
      
      if (!checkRes.data.can_proceed) {
        setCreditsModalData({
          blockingReason: checkRes.data.blocking_reason,
          creditsRequired: checkRes.data.credits.required,
          creditsAvailable: checkRes.data.credits.available,
          limitsData: checkRes.data.limits,
          planName: checkRes.data.plan_name
        });
        setShowCreditsModal(true);
        return;
      }
    } catch (e) {
      console.error("Failed to check limits:", e);
    }
    
    setRegeneratingCover(true);
    try {
      await api.post(`/stories/${storyId}/regenerate-cover`);
      // Deduct credits immediately in UI (2 credits for cover regenerate)
      deductCredits(2);
      toast.success("Cover regenerated successfully!");
      loadStory();
    } catch (e) {
      refreshCredits();
      if (e.response?.status === 402) {
        setCreditsModalData({
          blockingReason: "insufficient_credits",
          creditsRequired: 2,
          creditsAvailable: 0,
          limitsData: {},
          planName: "Free"
        });
        setShowCreditsModal(true);
      } else {
        toast.error("Failed to regenerate cover");
      }
    } finally {
      setRegeneratingCover(false);
    }
  };

  const handleRegenerateMissing = async () => {
    const missingCount = story?.pages?.filter(p => !p.image_url).length || 0;
    
    // Check credits before regenerating missing
    if (missingCount > 0) {
      try {
        const checkRes = await api.get(`/subscription/check-limits?action=illustration_regenerate&amount=${missingCount}`);
        
        if (!checkRes.data.can_proceed) {
          setCreditsModalData({
            blockingReason: checkRes.data.blocking_reason,
            creditsRequired: checkRes.data.credits.required,
            creditsAvailable: checkRes.data.credits.available,
            limitsData: checkRes.data.limits,
            planName: checkRes.data.plan_name
          });
          setShowCreditsModal(true);
          return;
        }
      } catch (e) {
        console.error("Failed to check limits:", e);
      }
    }
    
    setRegenerating(true);
    try {
      const res = await api.post(`/stories/${storyId}/regenerate-missing`);
      // Deduct credits immediately in UI (2 credits per missing image)
      deductCredits(res.data.missing_count * 2);
      toast.success(`Regenerating ${res.data.missing_count} missing illustrations... This may take a few minutes.`);
      // Reload story to get regenerating status
      await loadStory();
    } catch (e) {
      refreshCredits();
      console.error("Regenerate missing error:", e);
      if (e.response?.status === 402) {
        setCreditsModalData({
          blockingReason: "insufficient_credits",
          creditsRequired: missingCount * 2,
          creditsAvailable: 0,
          limitsData: {},
          planName: "Free"
        });
        setShowCreditsModal(true);
      } else {
        toast.error("Failed to start regeneration");
      }
    } finally {
      setRegenerating(false);
    }
  };

  // Regenerate all images (cover + all pages) for fixing broken images
  const handleRegenerateAllImages = async (coverOnly = false) => {
    const pageCount = story?.pages?.length || 0;
    const totalCount = coverOnly ? 1 : pageCount + 1; // +1 for cover
    
    // Check credits before regenerating
    try {
      const checkRes = await api.get(`/subscription/check-limits?action=illustration_regenerate&amount=${totalCount}`);
      
      if (!checkRes.data.can_proceed) {
        setCreditsModalData({
          blockingReason: checkRes.data.blocking_reason,
          creditsRequired: checkRes.data.credits.required,
          creditsAvailable: checkRes.data.credits.available,
          limitsData: checkRes.data.limits,
          planName: checkRes.data.plan_name
        });
        setShowCreditsModal(true);
        setShowFixImagesModal(false);
        return;
      }
    } catch (e) {
      console.error("Failed to check limits:", e);
    }
    
    setRegeneratingAll(true);
    setShowFixImagesModal(false);
    
    try {
      if (coverOnly) {
        // Only regenerate cover
        await api.post(`/stories/${storyId}/regenerate-cover`);
        deductCredits(2);
        toast.success("Cover is being regenerated...");
      } else {
        // Regenerate cover first
        toast.info("Starting full image regeneration... This may take several minutes.");
        await api.post(`/stories/${storyId}/regenerate-cover`);
        deductCredits(2);
        
        // Then regenerate each page
        for (let i = 0; i < pageCount; i++) {
          await api.post(`/stories/${storyId}/regenerate-page/${i + 1}`);
          deductCredits(2);
          toast.info(`Regenerating page ${i + 1}/${pageCount}...`);
        }
        
        toast.success("All images regenerated successfully!");
      }
      
      // Reload story to get updated images
      await loadStory();
    } catch (e) {
      refreshCredits();
      console.error("Regenerate all error:", e);
      if (e.response?.status === 402) {
        setCreditsModalData({
          blockingReason: "insufficient_credits",
          creditsRequired: totalCount * 2,
          creditsAvailable: 0,
          limitsData: {},
          planName: "Free"
        });
        setShowCreditsModal(true);
      } else {
        toast.error("Failed to regenerate images");
      }
    } finally {
      setRegeneratingAll(false);
    }
  };

  // Check if current user can fix images (owner or master admin)
  const canFixImages = !isSharedView && story && (
    isMasterAdmin || 
    (currentUserId && story.user_id === currentUserId)
  );

  // Edit Mode functionality
  const startEditMode = () => {
    setEditMode(true);
    setEditedTitle(story.title);
    // Initialize editedPages with current page texts
    const initialEdits = {};
    pages.forEach((page, index) => {
      initialEdits[index + 1] = page.text;
    });
    setEditedPages(initialEdits);
  };

  const cancelEditMode = () => {
    setEditMode(false);
    setEditedPages({});
    setEditedTitle("");
  };

  const handlePageTextChange = (pageNum, newText) => {
    setEditedPages(prev => ({
      ...prev,
      [pageNum]: newText
    }));
  };

  const saveAllEdits = async () => {
    setIsSaving(true);
    try {
      const savePromises = [];
      
      // Save title if changed
      if (editedTitle !== story.title) {
        savePromises.push(
          api.put(`/stories/${storyId}/title?title=${encodeURIComponent(editedTitle)}`)
        );
      }
      
      // Save each edited page
      for (const [pageNum, text] of Object.entries(editedPages)) {
        const originalText = pages[parseInt(pageNum) - 1]?.text;
        if (text !== originalText) {
          savePromises.push(
            api.put(`/stories/${storyId}/pages/${pageNum}/text`, { text })
          );
        }
      }
      
      if (savePromises.length === 0) {
        toast.info("No changes to save");
        setEditMode(false);
        return;
      }
      
      await Promise.all(savePromises);
      toast.success(`Saved ${savePromises.length} change(s)!`);
      
      // Reload story to get updated data
      await loadStory();
      setEditMode(false);
      setEditedPages({});
      setEditedTitle("");
    } catch (e) {
      console.error("Save error:", e);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Define pages early to avoid reference errors
  const pages = story?.pages || [];
  
  // Get the current text for a page (edited or original)
  const getPageText = useCallback((pageNum) => {
    if (editMode && editedPages[pageNum] !== undefined) {
      return editedPages[pageNum];
    }
    return pages[pageNum - 1]?.text || "";
  }, [editMode, editedPages, pages]);

  // AI TTS Narration functionality
  const playAiNarration = async (pageNum) => {
    // Check if we already have the audio
    if (aiNarrationAudio[pageNum]) {
      playStoredAudio(pageNum);
      return;
    }

    // Generate narration for this page
    setAiNarrationLoading(true);
    try {
      const res = await api.post(`/stories/${storyId}/narrate-page/${pageNum}`, null, {
        params: { voice: selectedAiVoice, speed: 0.9 }
      });
      
      // Store the audio
      setAiNarrationAudio(prev => ({
        ...prev,
        [pageNum]: res.data.audio
      }));
      
      // Play it
      playBase64Audio(res.data.audio);
    } catch (e) {
      console.error("Failed to generate narration:", e);
      toast.error("Failed to generate narration. Please try again.");
    } finally {
      setAiNarrationLoading(false);
    }
  };

  const playBase64Audio = (base64Audio) => {
    // Stop any currently playing audio
    if (aiAudioRef.current) {
      aiAudioRef.current.pause();
    }

    const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
    aiAudioRef.current = audio;
    setHasAudioLoaded(true);
    
    // Track play/pause state
    audio.onplay = () => setIsAiAudioPlaying(true);
    audio.onpause = () => setIsAiAudioPlaying(false);
    audio.onended = () => {
      setIsAiAudioPlaying(false);
      setHasAudioLoaded(false);
    };
    
    audio.play();
  };

  const playStoredAudio = (pageNum) => {
    const audio = aiNarrationAudio[pageNum];
    if (audio) {
      playBase64Audio(audio);
    }
  };

  const stopAiNarration = () => {
    if (aiAudioRef.current) {
      aiAudioRef.current.pause();
      aiAudioRef.current.currentTime = 0;
      aiAudioRef.current = null;
      setIsAiAudioPlaying(false);
      setHasAudioLoaded(false);
    }
  };

  const toggleAiNarrationPause = () => {
    if (aiAudioRef.current) {
      if (isAiAudioPlaying) {
        aiAudioRef.current.pause();
      } else {
        aiAudioRef.current.play();
      }
    }
  };

  // PDF Export functionality
  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    try {
      const response = await api.post(
        `/stories/${storyId}/export-pdf`,
        null,
        {
          params: { include_illustrations: true, page_size: "letter" },
          responseType: "blob"
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${story.title.replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully!");
      
      // Track achievement progress
      try {
        await api.post("/user/progress/track", null, { params: { action: "story_read", story_id: storyId } });
      } catch (e) {
        // Silent fail for tracking
      }
    } catch (e) {
      console.error("PDF export failed:", e);
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  // Share functionality
  const handleShare = async () => {
    setIsSharing(true);
    try {
      const res = await api.post(`/stories/${storyId}/share`);
      setShareToken(res.data.share_token);
      setShowShareModal(true);
      
      // Track achievement progress
      try {
        await api.post("/user/progress/track", null, { params: { action: "story_shared" } });
      } catch (e) {
        // Silent fail for tracking
      }
    } catch (e) {
      toast.error("Failed to enable sharing");
    } finally {
      setIsSharing(false);
    }
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/shared/${shareToken}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard!");
  };

  // Feature Story functionality
  const handleFeatureStory = async () => {
    setIsFeaturingStory(true);
    try {
      if (isFeatured) {
        await api.delete(`/stories/${storyId}/feature`);
        setIsFeatured(false);
        toast.success("Story removed from featured");
      } else {
        await api.post(`/stories/${storyId}/feature`);
        setIsFeatured(true);
        toast.success("🌟 Your story is now featured as Story of the Week!");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update feature status");
    } finally {
      setIsFeaturingStory(false);
    }
  };

  // TTS Functions
  const speakText = (text) => {
    if (!window.speechSynthesis) {
      toast.error("Text-to-speech not supported in this browser");
      return;
    }
    
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = VOICE_SPEEDS[voiceSpeed] || 1.0;
    utterance.pitch = 1.1;
    utterance.volume = 1.0;
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    utterance.onend = () => {
      if (readingMode && isNarrating) {
        const totalPages = story?.pages?.length || 0;
        setTimeout(() => {
          if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
          } else {
            setIsNarrating(false);
            toast.success("Story narration complete! 🌟");
          }
        }, 1000);
      }
    };
    
    window.speechSynthesis.speak(utterance);
  };

  // Enhanced swipe handlers for tablet and mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
    isSwiping.current = true;
    setSwipeDirection(null);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping.current) return;
    
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
    
    const diffX = touchStartX.current - touchEndX.current;
    const diffY = Math.abs(touchStartY.current - touchEndY.current);
    
    // Only show swipe indicator if horizontal swipe is dominant
    if (Math.abs(diffX) > 30 && Math.abs(diffX) > diffY) {
      setSwipeDirection(diffX > 0 ? 'left' : 'right');
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping.current) return;
    isSwiping.current = false;
    
    const totalPages = story?.pages?.length || 0;
    const swipeDistanceX = touchStartX.current - touchEndX.current;
    const swipeDistanceY = Math.abs(touchStartY.current - touchEndY.current);
    const minSwipeDistance = 50;

    // Only trigger if horizontal swipe is more than vertical (prevent scroll conflicts)
    if (Math.abs(swipeDistanceX) > minSwipeDistance && Math.abs(swipeDistanceX) > swipeDistanceY) {
      if (swipeDistanceX > 0 && currentPage < totalPages) {
        // Swipe left = next page
        setCurrentPage(prev => prev + 1);
      } else if (swipeDistanceX < 0 && currentPage > 0) {
        // Swipe right = previous page
        setCurrentPage(prev => prev - 1);
      }
    }
    
    // Reset
    touchStartX.current = 0;
    touchStartY.current = 0;
    touchEndX.current = 0;
    touchEndY.current = 0;
    setSwipeDirection(null);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (fullscreenMode || readingMode) {
        const totalPages = story?.pages?.length || 0;
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          if (currentPage > 0) setCurrentPage(prev => prev - 1);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          if (fullscreenMode) toggleFullscreen();
          if (readingMode) exitReadingMode();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenMode, readingMode, currentPage, story]);

  // Fullscreen toggle with better mobile support
  const toggleFullscreen = async () => {
    if (!fullscreenMode) {
      setFullscreenMode(true);
      // Try to enter fullscreen - works on most devices
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          // Safari/iOS
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) {
          // IE/Edge
          await elem.msRequestFullscreen();
        }
      } catch (e) {
        // Fullscreen not supported or denied, but still use our fullscreen UI
        console.log('Fullscreen API not available, using fullscreen UI mode');
      }
      // Lock screen orientation to landscape on mobile if supported
      try {
        if (screen.orientation?.lock) {
          await screen.orientation.lock('landscape').catch(() => {});
        }
      } catch (e) {
        // Screen orientation lock not supported
      }
    } else {
      setFullscreenMode(false);
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      } catch (e) {
        // Exit fullscreen failed silently
      }
      // Unlock screen orientation
      try {
        if (screen.orientation?.unlock) {
          screen.orientation.unlock();
        }
      } catch (e) {
        // Screen orientation unlock not supported
      }
    }
  };

  // Listen for fullscreen changes (user pressing Esc, etc.)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      if (!isFullscreen && fullscreenMode) {
        setFullscreenMode(false);
      }
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [fullscreenMode]);

  const startReadingMode = () => {
    setReadingMode(true);
    setCurrentPage(0);
    setIsPlaying(true);
    toast.success("Reading Mode started! Sit back and enjoy 📖");
  };

  const exitReadingMode = () => {
    setReadingMode(false);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <MagicLoader text="Loading storybook..." />
      </div>
    );
  }

  if (!story) return null;

  const GenreIcon = genreIcons[story.genre] || BookOpen;
  const totalPages = pages.length;
  const isGenerating = story.status === "generating" || story.status === "illustrating" || story.status === "regenerating";
  const currentPageData = currentPage === 0 ? null : pages[currentPage - 1];

  // Reading Mode UI
  if (readingMode) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 z-50" data-testid="reading-mode">
        {/* Stars background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                opacity: Math.random() * 0.7 + 0.3
              }}
            />
          ))}
        </div>
        
        {/* Controls overlay */}
        <div className="absolute top-4 right-4 z-20 flex items-center gap-3">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
            data-testid="reading-settings-btn"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button
            onClick={exitReadingMode}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
            data-testid="exit-reading-mode-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className="absolute top-16 right-4 z-20 bg-white/10 backdrop-blur-lg rounded-2xl p-6 w-72 text-white"
            >
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" /> Reading Settings
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-white/70 flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4" /> Page Duration: {pageInterval}s
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="20"
                    value={pageInterval}
                    onChange={(e) => setPageInterval(parseInt(e.target.value))}
                    className="w-full accent-violet-400"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-white/70 flex items-center gap-2 mb-2">
                    <Music className="w-4 h-4" /> Background Music
                  </label>
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={() => setMusicEnabled(!musicEnabled)}
                      className={`p-2 rounded-lg ${musicEnabled ? "bg-violet-500" : "bg-white/10"}`}
                    >
                      {musicEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                    </button>
                    {musicEnabled && (
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={musicVolume * 100}
                        onChange={(e) => setMusicVolume(parseInt(e.target.value) / 100)}
                        className="flex-1 accent-violet-400"
                      />
                    )}
                  </div>
                  {musicEnabled && (
                    <select
                      value={selectedMusic}
                      onChange={(e) => {
                        setSelectedMusic(e.target.value);
                        if (audioRef.current) {
                          audioRef.current.src = BACKGROUND_MUSIC[e.target.value];
                          if (isPlaying) audioRef.current.play();
                        }
                      }}
                      className="w-full bg-white/10 rounded-lg p-2 text-sm"
                    >
                      <option value="lullaby">Lullaby</option>
                      <option value="peaceful">Peaceful</option>
                      <option value="adventure">Adventure</option>
                    </select>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className="h-full flex flex-col items-center justify-center p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl w-full"
            >
              {currentPage === 0 ? (
                <div className="text-center">
                  <motion.div
                    initial={{ y: 20 }}
                    animate={{ y: 0 }}
                    className="mb-8"
                  >
                    {story.cover_image_url ? (
                      <img 
                        src={getImageUrl(story.cover_image_url)} 
                        alt="Cover" 
                        className="max-h-[50vh] mx-auto rounded-3xl shadow-2xl"
                      />
                    ) : (
                      <div className="w-64 h-64 mx-auto rounded-3xl bg-white/10 flex items-center justify-center">
                        <GenreIcon className="w-24 h-24 text-white/50" />
                      </div>
                    )}
                  </motion.div>
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    {story.title}
                  </h1>
                  <p className="text-white/60 text-lg">A {story.genre} story</p>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    {currentPageData?.image_url ? (
                      <img 
                        src={getImageUrl(currentPageData.image_url)} 
                        alt={`Page ${currentPage}`} 
                        className="max-h-[50vh] w-full object-contain rounded-3xl shadow-2xl"
                      />
                    ) : (
                      <div className="aspect-square max-w-md mx-auto rounded-3xl bg-white/10 flex items-center justify-center">
                        <Book className="w-24 h-24 text-white/30" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="text-2xl md:text-3xl text-white leading-relaxed story-text whitespace-pre-wrap">
                      {currentPageData?.text}
                    </div>
                    <p className="text-white/40 mt-6">Page {currentPage} of {totalPages}</p>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Playback Controls */}
          <div className="mt-12 flex items-center gap-6">
            <button
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="p-4 rounded-full bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-colors"
              data-testid="reading-prev-btn"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button
              onClick={togglePlayPause}
              className="p-6 rounded-full bg-violet-500 text-white hover:bg-violet-400 transition-colors shadow-lg"
              data-testid="reading-play-pause-btn"
            >
              {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
            </button>
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-4 rounded-full bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-colors"
              data-testid="reading-next-btn"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-8 w-full max-w-md">
            <div className="h-1 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-violet-400"
                initial={{ width: 0 }}
                animate={{ width: `${((currentPage) / totalPages) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fullscreen Book Mode - Optimized for tablet and mobile
  if (fullscreenMode) {
    return (
      <div 
        className="fixed inset-0 bg-gradient-to-br from-amber-50 via-white to-violet-50 z-50 flex flex-col touch-pan-y"
        data-testid="fullscreen-book-mode"
        ref={bookContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Swipe indicator overlay */}
        {swipeDirection && (
          <div className={`absolute inset-y-0 ${swipeDirection === 'left' ? 'right-0' : 'left-0'} w-16 bg-gradient-to-${swipeDirection === 'left' ? 'l' : 'r'} from-violet-200/50 to-transparent z-30 pointer-events-none flex items-center ${swipeDirection === 'left' ? 'justify-end pr-2' : 'justify-start pl-2'}`}>
            {swipeDirection === 'left' ? (
              <ChevronRight className="w-8 h-8 text-violet-500 animate-pulse" />
            ) : (
              <ChevronLeft className="w-8 h-8 text-violet-500 animate-pulse" />
            )}
          </div>
        )}

        {/* Minimal header - auto-hide on mobile after a few seconds */}
        <div className="absolute top-2 sm:top-4 left-2 sm:left-4 right-2 sm:right-4 z-20 flex items-center justify-between">
          <button
            onClick={toggleFullscreen}
            className="p-2 sm:p-3 rounded-full bg-white/90 backdrop-blur-sm shadow-lg text-slate-700 hover:bg-white transition-colors active:scale-95"
            data-testid="exit-fullscreen-btn"
          >
            <Minimize className="w-5 h-5" />
          </button>
          <div className="text-slate-600 text-xs sm:text-sm font-medium bg-white/90 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg">
            {currentPage === 0 ? "Cover" : `${currentPage} / ${totalPages}`}
          </div>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 sm:p-3 rounded-full bg-white/90 backdrop-blur-sm shadow-lg text-slate-700 hover:bg-white transition-colors active:scale-95"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>

        {/* Book content - touch-optimized */}
        <div className="flex-1 flex items-center justify-center p-2 sm:p-4 md:p-6 pt-16 sm:pt-20">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, x: swipeDirection === 'left' ? 100 : -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: swipeDirection === 'left' ? -100 : 100 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="w-full h-full max-w-6xl flex items-center justify-center"
            >
              {currentPage === 0 ? (
                /* Cover Page - Image dominant */
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden w-full max-h-[82vh] flex flex-col sm:flex-row">
                  <div className="sm:w-1/3 p-4 sm:p-6 md:p-8 flex flex-col justify-center items-center bg-gradient-to-br from-violet-600 to-violet-800 text-white min-h-[20vh] sm:min-h-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3 sm:mb-4">
                      <GenreIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-2 px-2" style={{ fontFamily: 'var(--font-heading)' }}>
                      {story.title}
                    </h1>
                    <p className="text-violet-200 text-xs sm:text-sm">A {story.genre} story</p>
                  </div>
                  {story.cover_image_url && (
                    <div className="sm:w-2/3 min-h-[50vh] sm:min-h-0">
                      <img src={getImageUrl(story.cover_image_url)} alt="Cover" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              ) : (
                /* Story Pages - Image takes 2/3, text takes 1/3 */
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden w-full max-h-[82vh] flex flex-col sm:flex-row">
                  {/* Illustration - Primary focus (2/3 on desktop) */}
                  <div className="sm:w-2/3 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-2 sm:p-4 min-h-[45vh] sm:min-h-0">
                    {currentPageData?.image_url ? (
                      <img 
                        src={getImageUrl(currentPageData.image_url)} 
                        alt={`Page ${currentPage}`}
                        className="max-h-[45vh] sm:max-h-[75vh] max-w-full object-contain rounded-xl sm:rounded-2xl shadow-lg"
                      />
                    ) : (
                      <div className="w-full aspect-square max-w-[250px] sm:max-w-lg rounded-xl sm:rounded-2xl bg-slate-200 flex items-center justify-center">
                        <Book className="w-16 h-16 sm:w-24 sm:h-24 text-slate-300" />
                      </div>
                    )}
                  </div>
                  {/* Story Text - Compact (1/3 on desktop) */}
                  <div className="sm:w-1/3 p-4 sm:p-6 flex flex-col justify-center bg-white overflow-y-auto max-h-[30vh] sm:max-h-none">
                    <div className="text-base sm:text-lg md:text-xl text-slate-800 leading-relaxed story-text whitespace-pre-wrap">
                      {currentPageData?.text}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Touch-friendly navigation buttons - smaller and more transparent on mobile */}
        <div className="absolute inset-y-0 left-0 flex items-center pl-1 sm:pl-2 md:pl-4 pointer-events-none">
          <button
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="pointer-events-auto p-2 sm:p-3 md:p-5 rounded-full bg-white/40 sm:bg-white/60 md:bg-white/80 backdrop-blur-sm shadow-md sm:shadow-lg text-slate-500 sm:text-slate-600 md:text-slate-700 disabled:opacity-10 hover:bg-white/70 active:scale-95 transition-all"
            data-testid="fullscreen-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-7 md:h-7" />
          </button>
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center pr-1 sm:pr-2 md:pr-4 pointer-events-none">
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="pointer-events-auto p-2 sm:p-3 md:p-5 rounded-full bg-white/40 sm:bg-white/60 md:bg-white/80 backdrop-blur-sm shadow-md sm:shadow-lg text-slate-500 sm:text-slate-600 md:text-slate-700 disabled:opacity-10 hover:bg-white/70 active:scale-95 transition-all"
            data-testid="fullscreen-next-btn"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-7 md:h-7" />
          </button>
        </div>

        {/* Swipe hint for mobile - only shown initially */}
        <div className="absolute bottom-16 sm:bottom-12 left-1/2 -translate-x-1/2 text-slate-400 text-xs sm:text-sm md:hidden flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full">
          <ChevronLeft className="w-4 h-4" />
          <span>Swipe to turn pages</span>
          <ChevronRight className="w-4 h-4" />
        </div>

        {/* Progress dots - touch friendly */}
        <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 sm:gap-1.5 bg-white/90 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full shadow-lg max-w-[90vw] overflow-x-auto">
          {[0, ...pages.map((_, i) => i + 1)].slice(0, Math.min(15, totalPages + 1)).map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all flex-shrink-0 ${
                currentPage === pageNum 
                  ? "bg-violet-600 scale-125" 
                  : "bg-slate-300 hover:bg-slate-400 active:bg-slate-500"
              }`}
              aria-label={`Go to page ${pageNum}`}
            />
          ))}
          {totalPages > 14 && (
            <span className="text-xs text-slate-500 ml-1 flex-shrink-0">+{totalPages - 14}</span>
          )}
        </div>
      </div>
    );
  }

  // Normal view
  return (
    <PageTransition>
      <div className="min-h-screen pt-20 pb-8 px-4 bg-gradient-to-br from-violet-50 via-white to-amber-50" data-testid="story-reader">
        <div className="max-w-5xl mx-auto">
          {/* Status Banner */}
          {isGenerating && (
            <div className="mb-6 bg-amber-100 border border-amber-300 rounded-2xl p-4">
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                <span className="text-amber-800 font-medium">
                  {story.status === "generating" ? "Writing your story..." : 
                   story.status === "regenerating" ? `Regenerating illustrations (${story.illustration_progress || "..."})` :
                   `Creating illustrations (${story.illustration_progress || `${pages.filter(p => p.image_url).length}/${totalPages}`})`}
                </span>
              </div>
              {story.status === "illustrating" && (
                <div className="mt-2 text-center text-sm text-amber-700">
                  ⏱️ Estimated: ~{Math.max(1, (totalPages - pages.filter(p => p.image_url).length) * 0.5).toFixed(0)}-{Math.max(2, (totalPages - pages.filter(p => p.image_url).length) * 1).toFixed(0)} minutes remaining
                </div>
              )}
            </div>
          )}

          {/* Partial Status Banner - Some illustrations failed */}
          {story.status === "partial" && !isSharedView && pages.filter(p => !p.image_url).length > 0 && (
            <div className="mb-6 bg-orange-100 border border-orange-300 rounded-2xl p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 text-orange-600" />
                  <span className="text-orange-800 font-medium">
                    Some illustrations could not be generated ({pages.filter(p => !p.image_url).length} missing)
                  </span>
                </div>
                <button
                  onClick={handleRegenerateMissing}
                  disabled={regenerating}
                  className="px-4 py-2 rounded-xl bg-orange-600 text-white font-medium hover:bg-orange-500 transition-colors flex items-center gap-2"
                  data-testid="regenerate-missing-btn"
                >
                  {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Regenerate Missing
                </button>
              </div>
            </div>
          )}

          {/* Edit Mode Banner */}
          {editMode && (
            <div className="mb-6 bg-gradient-to-r from-pink-100 to-rose-100 border border-pink-300 rounded-2xl p-4">
              <div className="flex items-center justify-center gap-3">
                <Pencil className="w-5 h-5 text-pink-600" />
                <span className="text-pink-800 font-medium">
                  Edit Mode Active — Navigate between pages to edit text, then click &quot;Save Changes&quot; when done
                </span>
              </div>
            </div>
          )}

          {/* Mode Buttons */}
          {!isGenerating && (story.status === "complete" || story.status === "partial") && !isSharedView && (
            <div className="mb-6 flex flex-wrap justify-center gap-4">
              {!editMode ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={startEditMode}
                    className="px-6 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold shadow-lg flex items-center gap-2 hover:from-pink-400 hover:to-rose-400 transition-all"
                    data-testid="start-edit-mode-btn"
                  >
                    <Pencil className="w-5 h-5" />
                    Edit Story
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={startReadingMode}
                    className="px-6 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold shadow-lg flex items-center gap-2 hover:from-indigo-500 hover:to-violet-500 transition-all"
                    data-testid="start-reading-mode-btn"
                  >
                    <Moon className="w-5 h-5" />
                    Bedtime Mode
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={toggleFullscreen}
                    className="px-6 py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold shadow-lg flex items-center gap-2 hover:from-emerald-500 hover:to-teal-500 transition-all"
                    data-testid="start-fullscreen-btn"
                  >
                    <Maximize className="w-5 h-5" />
                    Fullscreen Book
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleShare}
                    disabled={isSharing}
                    className="px-6 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg flex items-center gap-2 hover:from-amber-400 hover:to-orange-400 transition-all disabled:opacity-50"
                    data-testid="share-story-btn"
                  >
                    {isSharing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />}
                    Share Story
                  </motion.button>
                  
                  {/* PDF Export Button */}
                  {story.status === "complete" && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleExportPdf}
                      disabled={isExportingPdf}
                      className="px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold shadow-lg flex items-center gap-2 hover:from-blue-400 hover:to-cyan-400 transition-all disabled:opacity-50"
                      data-testid="export-pdf-btn"
                    >
                      {isExportingPdf ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                      {isExportingPdf ? "Exporting..." : "Download PDF"}
                    </motion.button>
                  )}
                  
                  {/* Print Book Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(`/print/${story?.story_id}`)}
                    className="px-6 py-3 rounded-2xl bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold shadow-lg flex items-center gap-2 hover:from-purple-400 hover:to-indigo-400 transition-all"
                    data-testid="print-book-btn"
                  >
                    <Printer className="w-5 h-5" />
                    Print Book
                  </motion.button>
                </>
              ) : (
                <>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={saveAllEdits}
                    disabled={isSaving}
                    className="px-6 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold shadow-lg flex items-center gap-2 hover:from-emerald-400 hover:to-green-400 transition-all disabled:opacity-50"
                    data-testid="save-edits-btn"
                  >
                    {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    {isSaving ? "Saving..." : "Save Changes"}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={cancelEditMode}
                    disabled={isSaving}
                    className="px-6 py-3 rounded-2xl bg-gradient-to-r from-slate-500 to-slate-600 text-white font-bold shadow-lg flex items-center gap-2 hover:from-slate-400 hover:to-slate-500 transition-all disabled:opacity-50"
                    data-testid="cancel-edit-btn"
                  >
                    <XCircle className="w-5 h-5" />
                    Cancel
                  </motion.button>
                </>
              )}
            </div>
          )}

          {/* Shared view fullscreen button */}
          {isSharedView && (story.status === "complete" || story.status === "partial") && (
            <div className="mb-6 flex justify-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={toggleFullscreen}
                className="px-6 py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold shadow-lg flex items-center gap-2 hover:from-emerald-500 hover:to-teal-500 transition-all"
                data-testid="shared-fullscreen-btn"
              >
                <Maximize className="w-5 h-5" />
                Fullscreen Book
              </motion.button>
            </div>
          )}

          {/* Share Modal */}
          <AnimatePresence>
            {showShareModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowShareModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white rounded-3xl p-8 max-w-md w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                      <Share2 className="w-8 h-8 text-amber-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                      Share Your Story
                    </h2>
                    <p className="text-slate-600 mt-2">Anyone with this link can view the story</p>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
                    <Link2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/shared/${shareToken}`}
                      className="flex-1 bg-transparent text-sm text-slate-600 outline-none"
                    />
                    <button
                      onClick={copyShareLink}
                      className="px-4 py-2 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-500 transition-colors flex items-center gap-2"
                      data-testid="copy-link-btn"
                    >
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>

                  <button
                    onClick={() => setShowShareModal(false)}
                    className="w-full mt-6 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors"
                  >
                    Done
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Book Container - with swipe support */}
          <div 
            className="bg-white rounded-3xl shadow-2xl overflow-hidden touch-pan-y"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Page Content */}
            <div className="relative aspect-[16/10] sm:aspect-[16/10] bg-gradient-to-br from-slate-50 to-slate-100">
              {/* Swipe indicator */}
              {swipeDirection && (
                <div className={`absolute inset-y-0 ${swipeDirection === 'left' ? 'right-0' : 'left-0'} w-12 bg-gradient-to-${swipeDirection === 'left' ? 'l' : 'r'} from-violet-200/60 to-transparent z-30 pointer-events-none flex items-center ${swipeDirection === 'left' ? 'justify-end pr-1' : 'justify-start pl-1'}`}>
                  {swipeDirection === 'left' ? (
                    <ChevronRight className="w-6 h-6 text-violet-500 animate-pulse" />
                  ) : (
                    <ChevronLeft className="w-6 h-6 text-violet-500 animate-pulse" />
                  )}
                </div>
              )}
              
              <AnimatePresence mode="wait">
                {currentPage === 0 ? (
                  <motion.div
                    key="cover"
                    initial={{ opacity: 0, x: swipeDirection === 'left' ? 100 : -100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: swipeDirection === 'left' ? -100 : 100 }}
                    transition={{ duration: 0.25 }}
                    className="absolute inset-0 flex"
                  >
                    {/* Cover - Title area (1/3) */}
                    <div className="w-1/3 p-6 flex flex-col justify-center items-center bg-gradient-to-br from-violet-600 to-violet-800 text-white">
                      <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-4">
                        <GenreIcon className="w-7 h-7 text-white" />
                      </div>
                      {editMode ? (
                        <div className="w-full">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <Pencil className="w-4 h-4 text-pink-300" />
                            <span className="text-xs font-medium text-pink-200">Edit Title</span>
                          </div>
                          <input
                            type="text"
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            className="w-full text-xl md:text-2xl font-bold text-center bg-white/20 border-2 border-pink-300 rounded-xl px-3 py-2 text-white placeholder-white/50 focus:border-pink-400 focus:ring-2 focus:ring-pink-300 outline-none"
                            placeholder="Story title..."
                            style={{ fontFamily: 'var(--font-heading)' }}
                            data-testid="edit-title"
                          />
                        </div>
                      ) : (
                        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-center mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                          {story.title}
                        </h1>
                      )}
                      <div className="flex items-center gap-2 text-violet-200 text-xs mt-3">
                        <span className="capitalize">{story.genre}</span>
                        <span>•</span>
                        <span>{totalPages} pages</span>
                      </div>
                    </div>
                    {/* Cover Image (2/3) */}
                    <div className="w-2/3 relative">
                      {story.cover_image_url ? (
                        <>
                          <img src={getImageUrl(story.cover_image_url)} alt="Cover" className="w-full h-full object-cover" />
                          {!isSharedView && !isGenerating && (
                            <button
                              onClick={handleRegenerateCover}
                              disabled={regeneratingCover}
                              className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl text-sm font-medium text-slate-700 hover:bg-white transition-colors flex items-center gap-2"
                              data-testid="regenerate-cover-btn"
                            >
                              {regeneratingCover ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Image className="w-4 h-4" />
                              )}
                              {regeneratingCover ? "Regenerating..." : "New Cover"}
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-violet-100">
                          {isGenerating ? (
                            <MagicLoader text="Creating cover..." />
                          ) : (
                            <div className="text-center">
                              <Book className="w-24 h-24 text-violet-300 mx-auto mb-4" />
                              {!isSharedView && (
                                <button
                                  onClick={handleRegenerateCover}
                                  disabled={regeneratingCover}
                                  className="text-violet-600 font-medium hover:underline flex items-center gap-2 mx-auto"
                                >
                                  {regeneratingCover ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Image className="w-4 h-4" />
                                  )}
                                  {regeneratingCover ? "Generating..." : "Generate Cover"}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key={`page-${currentPage}`}
                    initial={{ opacity: 0, x: swipeDirection === 'left' ? 100 : -100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: swipeDirection === 'left' ? -100 : 100 }}
                    transition={{ duration: 0.25 }}
                    className="absolute inset-0 flex"
                  >
                    {/* Illustration (2/3) - Primary focus */}
                    <div className="w-2/3 relative bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
                      {currentPageData?.image_url ? (
                        <>
                          <img 
                            src={getImageUrl(currentPageData.image_url)} 
                            alt={`Page ${currentPage}`} 
                            className="max-w-full max-h-full object-contain rounded-xl shadow-lg"
                          />
                          {!isSharedView && (
                            <div className="absolute bottom-4 right-4 flex gap-2">
                              <button
                                onClick={() => openModifyModal(currentPage)}
                                disabled={regenerating || isModifying}
                                className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl text-sm font-medium text-violet-700 hover:bg-violet-50 transition-colors flex items-center gap-2"
                                data-testid={`modify-page-${currentPage}`}
                                title="Modify this image with a custom prompt"
                              >
                                <Pencil className={`w-4 h-4 ${isModifying ? "animate-pulse" : ""}`} />
                                Modify
                              </button>
                              <button
                                onClick={() => handleRegeneratePage(currentPage)}
                                disabled={regenerating || isModifying}
                                className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl text-sm font-medium text-slate-700 hover:bg-white transition-colors flex items-center gap-2"
                                data-testid={`regenerate-page-${currentPage}`}
                              >
                                <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
                                Regenerate
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {isGenerating ? (
                            <MagicLoader text="Creating illustration..." />
                          ) : (
                            <div className="text-center">
                              <Book className="w-16 h-16 text-slate-300 mx-auto mb-3" />
                              {!isSharedView && (
                                <button
                                  onClick={() => handleRegeneratePage(currentPage)}
                                  disabled={regenerating}
                                  className="text-violet-600 font-medium hover:underline flex items-center gap-2"
                                >
                                  <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
                                  Generate Illustration
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Story Text (1/3) - Compact */}
                    <div className="w-1/3 p-6 flex flex-col justify-center bg-white">
                      {editMode ? (
                        <div className="flex flex-col h-full">
                          <div className="flex items-center gap-2 mb-3">
                            <Pencil className="w-4 h-4 text-pink-500" />
                            <span className="text-sm font-medium text-pink-600">Editing Page {currentPage}</span>
                          </div>
                          <textarea
                            value={getPageText(currentPage)}
                            onChange={(e) => handlePageTextChange(currentPage, e.target.value)}
                            className="flex-1 w-full p-3 text-base text-slate-800 leading-relaxed border-2 border-pink-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none resize-none story-text"
                            placeholder="Enter story text..."
                            data-testid={`edit-page-${currentPage}`}
                          />
                          <div className="mt-3 text-slate-400 text-xs flex justify-between">
                            <span>Page {currentPage} of {totalPages}</span>
                            <span className="text-pink-500">{getPageText(currentPage).length} chars</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="story-text text-base md:text-lg text-slate-800 leading-relaxed whitespace-pre-wrap">
                            {currentPageData?.text || "..."}
                          </div>
                          <div className="mt-6 flex items-center justify-between">
                            <span className="text-slate-400 text-xs">Page {currentPage} of {totalPages}</span>
                            {!isSharedView && currentPageData?.text && (
                              <div className="flex items-center gap-2">
                                {/* Voice Selector */}
                                <div className="relative">
                                  <button
                                    onClick={() => setShowVoiceSelector(!showVoiceSelector)}
                                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium hover:bg-slate-200 transition-colors"
                                    title="Choose voice"
                                    data-testid="voice-selector-btn"
                                  >
                                    <Settings className="w-3.5 h-3.5" />
                                    {aiVoices.find(v => v.id === selectedAiVoice)?.name || "Voice"}
                                  </button>
                                  
                                  {showVoiceSelector && (
                                    <div className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 z-20 overflow-hidden">
                                      <div className="p-2 border-b border-slate-100">
                                        <span className="text-xs font-medium text-slate-500">Select Voice</span>
                                      </div>
                                      <div className="max-h-48 overflow-y-auto">
                                        {aiVoices.map(voice => (
                                          <button
                                            key={voice.id}
                                            onClick={() => {
                                              setSelectedAiVoice(voice.id);
                                              setShowVoiceSelector(false);
                                              // Clear cached audio when voice changes
                                              setAiNarrationAudio({});
                                            }}
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                                              selectedAiVoice === voice.id ? "bg-violet-50 text-violet-700" : "text-slate-700"
                                            }`}
                                            data-testid={`voice-option-${voice.id}`}
                                          >
                                            <div className="font-medium">{voice.name}</div>
                                            <div className="text-xs text-slate-400">{voice.description}</div>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Playback Controls */}
                                {hasAudioLoaded ? (
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={toggleAiNarrationPause}
                                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 text-sm font-medium hover:bg-violet-200 transition-colors"
                                      title={isAiAudioPlaying ? "Pause" : "Resume"}
                                      data-testid="tts-pause-resume-btn"
                                    >
                                      {isAiAudioPlaying ? (
                                        <Pause className="w-4 h-4" />
                                      ) : (
                                        <Play className="w-4 h-4" />
                                      )}
                                      {isAiAudioPlaying ? "Pause" : "Resume"}
                                    </button>
                                    <button
                                      onClick={stopAiNarration}
                                      className="p-1.5 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                      title="Stop"
                                      data-testid="tts-stop-btn"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => playAiNarration(currentPage)}
                                    disabled={aiNarrationLoading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 text-sm font-medium hover:bg-violet-200 transition-colors disabled:opacity-50"
                                    title="Read this page aloud"
                                    data-testid={`read-aloud-page-${currentPage}`}
                                  >
                                    {aiNarrationLoading ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Mic className="w-4 h-4" />
                                    )}
                                    {aiNarrationLoading ? "Loading..." : "Read Aloud"}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Navigation */}
            <div className="p-6 bg-white border-t border-slate-100">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-colors ${
                    currentPage === 0 ? "text-slate-300 cursor-not-allowed" : "text-slate-700 hover:bg-slate-100"
                  }`}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-5 h-5" /> Previous
                </button>

                <div className="flex items-center gap-2 overflow-x-auto max-w-md px-4">
                  {[0, ...pages.map((_, i) => i + 1)].map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-3 h-3 rounded-full transition-colors flex-shrink-0 ${
                        currentPage === pageNum ? "bg-violet-600" : "bg-slate-200 hover:bg-slate-300"
                      }`}
                      data-testid={`page-dot-${pageNum}`}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-colors ${
                    currentPage === totalPages ? "text-slate-300 cursor-not-allowed" : "text-slate-700 hover:bg-slate-100"
                  }`}
                  data-testid="next-page-btn"
                >
                  Next <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4 mt-8 flex-wrap">
            {isSharedView ? (
              <button
                onClick={() => navigate("/")}
                className="px-6 py-3 rounded-xl bg-violet-600 text-white font-medium hover:bg-violet-500 transition-colors shadow-sm"
              >
                Create Your Own Story
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="px-6 py-3 rounded-xl bg-white text-slate-700 font-medium hover:bg-slate-50 transition-colors shadow-sm"
                >
                  Back to Dashboard
                </button>
                {canFixImages && (
                  <button
                    onClick={() => setShowFixImagesModal(true)}
                    disabled={regeneratingAll}
                    className="px-6 py-3 rounded-xl bg-amber-50 text-amber-700 font-medium hover:bg-amber-100 transition-colors flex items-center gap-2 disabled:opacity-50"
                    data-testid="fix-images-btn"
                  >
                    {regeneratingAll ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-5 h-5" />
                    )}
                    {regeneratingAll ? "Regenerating..." : "Fix Broken Images"}
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  className="px-6 py-3 rounded-xl bg-red-50 text-red-600 font-medium hover:bg-red-100 transition-colors flex items-center gap-2"
                  data-testid="delete-story-btn"
                >
                  <Trash2 className="w-5 h-5" /> Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Fix Broken Images Modal */}
      <AnimatePresence>
        {showFixImagesModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowFixImagesModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Image className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Fix Broken Images</h3>
                  <p className="text-sm text-slate-500">Regenerate illustrations for this story</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This will use credits to regenerate images. 
                  Each image costs 2 credits.
                </p>
                <p className="text-xs text-amber-600 mt-2">
                  • Cover only: 2 credits<br />
                  • All images ({(story?.pages?.length || 0) + 1} total): {((story?.pages?.length || 0) + 1) * 2} credits
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleRegenerateAllImages(true)}
                  className="w-full px-4 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                  data-testid="regenerate-cover-only-btn"
                >
                  <Image className="w-5 h-5" />
                  Regenerate Cover Only
                </button>
                <button
                  onClick={() => handleRegenerateAllImages(false)}
                  className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium hover:from-amber-600 hover:to-orange-600 transition-colors flex items-center justify-center gap-2"
                  data-testid="regenerate-all-images-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  Regenerate All Images
                </button>
              </div>

              <button
                onClick={() => setShowFixImagesModal(false)}
                className="w-full mt-4 px-4 py-2 text-slate-500 hover:text-slate-700 transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modify Image Modal */}
      <AnimatePresence>
        {showModifyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModifyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Pencil className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                      Modify Illustration
                    </h3>
                    <p className="text-sm text-slate-500">Page {modifyPageNum}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModifyModal(false)}
                  className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Describe the change you want to make:
                </label>
                <textarea
                  value={modificationPrompt}
                  onChange={(e) => setModificationPrompt(e.target.value)}
                  placeholder="e.g., 'Change the coat color to red', 'Add a rainbow in the background', 'Make the character smile more'"
                  className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                  rows={3}
                  maxLength={500}
                  data-testid="modify-prompt-input"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">
                  {modificationPrompt.length}/500 characters
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Be specific about what you want to change. The AI will try to keep everything else the same.
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  Cost: <span className="font-medium text-violet-600">2 credits</span>
                </span>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowModifyModal(false)}
                    className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
                    disabled={isModifying}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleModifyImage}
                    disabled={isModifying || !modificationPrompt.trim()}
                    className="px-4 py-2 rounded-xl bg-violet-600 text-white font-medium hover:bg-violet-500 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="confirm-modify-btn"
                  >
                    {isModifying ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Modifying...
                      </>
                    ) : (
                      <>
                        <Pencil className="w-4 h-4" />
                        Apply Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Credits Modal */}
      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        blockingReason={creditsModalData.blockingReason}
        creditsRequired={creditsModalData.creditsRequired}
        creditsAvailable={creditsModalData.creditsAvailable}
        limitsData={creditsModalData.limitsData}
        planName={creditsModalData.planName}
      />
    </PageTransition>
  );
};

export default StoryReaderPage;
